import { React, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  styled,
  useTheme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { S3DataMain } from "../../S3Data";
import seedrandom from "seedrandom";
import { hasPhotoForDay, hasTextForDay } from "../../photoDays";

const StyledMainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: "calc(100vh - 8rem)",
  padding: "2rem 0 1rem",
  [theme.breakpoints.down("sm")]: {
    minHeight: "auto",
    paddingTop: "0.75rem",
  },
}));

const StyledMainCard = styled(Card)(({ theme }) => ({
  width: "min(760px, 100%)",
  margin: "auto",
  borderRadius: "2.25rem",
  background: "rgba(255, 248, 239, 0.86)",
  border: "1px solid rgba(159, 104, 72, 0.16)",
  boxShadow: "0 24px 55px rgba(90, 60, 41, 0.12)",
  overflow: "hidden",
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: "2.5rem 2.75rem 2.75rem",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: "1.75rem 1.2rem 1.4rem",
  },
}));

const DayLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Fira Sans Extra Condensed', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.24em",
  fontSize: "0.82rem",
  color: theme.palette.primary.main,
  marginBottom: "0.85rem",
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Fira Sans Extra Condensed', sans-serif",
  fontSize: "clamp(2.8rem, 6vw, 4.6rem)",
  lineHeight: 0.95,
  color: theme.palette.text.primary,
  marginBottom: "1.5rem",
}));

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(1.1rem, 2vw, 1.35rem)",
  lineHeight: 1.85,
  color: theme.palette.text.secondary,
  maxWidth: "34rem",
  margin: "0 auto",
}));

const StyledImage = styled("img")({
  borderRadius: "1.5rem",
  objectFit: "cover",
  margin: "2rem auto 0",
  boxShadow: "0 20px 40px rgba(93, 62, 44, 0.16)",
});

function Main() {
  const baseUrl = "https://daily-sarah.s3.amazonaws.com/";
  const imgExt = "photos/";
  const theme = useTheme();
  const [text, setText] = useState("");
  const [img, setImg] = useState("");
  const [aspectRatio, setAspectRatio] = useState(1);
  //let date_1 = new Date("4/18/2023");
  let date_2 = new Date();
 // let difference = date_2.getTime() - date_1.getTime();
 const  currentDate  = date_2;
 const dateSeed = currentDate.toISOString().slice(0, 10); // Use the date as a seed
  // Create a consistent random number generator with the date seed
  const generator = seedrandom(dateSeed);
  // Generate a random integer between 1 and 121 (inclusive)
  const randomValue = Math.floor(generator() * 121) + 1;
  let day = randomValue;
  //let day = Math.ceil(difference / (1000 * 3600 * 24));

  const sm = useMediaQuery(theme.breakpoints.down("sm"));

  //Fetch text data from s3 bucket on load
  useEffect(() => {
    async function fetchData() {
      if (hasTextForDay(day)) {
        const textData = await S3DataMain(`text/${day}.txt`);
        setText(textData ? textData.Body.toString() : "");
      } else {
        setText("");
      }

      setImg(hasPhotoForDay(day) ? `${baseUrl}${imgExt}${day}` : "");
    }
    fetchData();

    //setImg(`${baseUrl}${imgExt}4`);
  }, [baseUrl, day, imgExt]);

  //get Image aspect ratio
  const getMeta = (url, cb) => {
    const img = new Image();
    img.onload = () => cb(null, img);
    img.onerror = (err) => cb(err);
    img.src = url;
  };

  useEffect(() => {
    if (!hasPhotoForDay(day)) {
      setAspectRatio(1);
      return;
    }

    getMeta(`${baseUrl}${imgExt}${day}`, (err, img) => {
      if (err) {
        setAspectRatio(1);
        return;
      }
      setAspectRatio(img.naturalWidth / img.naturalHeight);
    });
  }, [baseUrl, day, imgExt]);

  return (
    <StyledMainContainer>
      <StyledMainCard>
        <StyledCardContent>
          <DayLabel>Daily Reflection</DayLabel>
          <TitleText>Day {day}</TitleText>
          {text && <BodyText>{text}</BodyText>}
          {img && (
            <StyledImage
              alt=""
              style={
                aspectRatio > 0
                  ? {
                      width: sm
                        ? (200 * aspectRatio).toString() + "px"
                        : (320 * aspectRatio).toString() + "px",
                      height: sm ? "200px" : "320px",
                    }
                  : {
                      width: sm ? "200px" : "320px",
                      height: sm
                        ? (200 * aspectRatio).toString() + "px"
                        : (320 * aspectRatio).toString() + "px",
                    }
              }
              src={img}
            />
          )}
        </StyledCardContent>
      </StyledMainCard>
    </StyledMainContainer>
  );
}

export default Main;
